import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { getColor, isDark } from '@lucid/theme';
import { isDefined } from '@lucid/utils';
import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../../types/types';
import Box from '../../box/box';
import Container from '../../container/container';
import Heading from '../../heading/heading';
import Image from '../../image/image';
import Text from '../../text/text';
import Link from '../../link/link';
import CtaWithSSO from '../cta-with-sso/cta-with-sso';
import LayoutContext from '../../context/layout-context';
import preFooterBackground from '../../../../../gatsby/co/src/assets/preFooterBackground.svg';

export interface PreFooterCtaProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The background color for the Pre Footer CTA - Use stringified theme value */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The available variants for the Link component. */
  buttonVariant?: Lucid.Theme.Component.Link.VariantKeys | Lucid.Theme.Component.Button.VariantKeys;
  /** The header (h2) for the Pre Footer CTA */
  heading?: string | null;
  /** The heading variang for the Pre Footer CTA */
  headingVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** The optional image for the Pre Footer CTA */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** Component should use gradient for select designs */
  hasGradient?: boolean;
  /** The text for the Pre Footer CTA */
  text?: string | null;
  /** Smaller text to display below the text */
  pitch?: string | null;
  /** Determines if the CTA should display as a button */
  displayAsButton?: boolean;
  /** Determines whether or not SSO buttons should be shown */
  hasSSO?: boolean;
  /** The CTA Button(s) for the Pre Footer CTA (defaults to sales form if none provided) */
  ctas?: Lucid.Directus.Generated.Links[];
  /** The available variants for the `pre-footer-cta` component. */
  variant?: 'primary' | 'secondary';
}

const PreFooterCta: FC<PropsWithChildren<PreFooterCtaProps>> = ({
  className,
  backgroundColor = 'white',
  displayAsButton = true,
  buttonVariant,
  hasGradient,
  heading,
  headingVariant,
  image,
  text,
  pitch,
  hasSSO,
  ctas,
  variant,
  ...rest
}) => {
  const { product } = useContext(LayoutContext);
  const backgroundColorValue = (typeof backgroundColor === 'object' && backgroundColor[product]) || backgroundColor;
  const hasDarkBackground = isDark(backgroundColorValue);
  const filteredCtas = ctas?.filter(cta => isDefined(cta?.default_link_text));

  return (
    <Box
      {...rest}
      css={theme => [
        {
          label: 'preFooterCTA',
          backgroundColor: getColor({
            color: backgroundColor,
            theme,
          }),
        },
        hasGradient && {
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -450,
            left: -250,
            width: '150%',
            height: '150%',
            background: `linear-gradient(180deg, ${theme.colors.coolGrey[hasDarkBackground ? 90 : 10]} 44.68%, ${theme.colors.coolGrey[hasDarkBackground ? 90 : 0]} 100%)`,
            zIndex: 1,
            opacity: 0.7,
            transformOrigin: '0 0',
            transform: 'rotate(-10deg)',
            [theme.breakpoints.bp500.min]: {
              top: -550,
              left: -350,
            },
            [theme.breakpoints.bp1024.min]: {
              top: -275,
              left: -275,
            },
          },
        },
        variant === 'secondary' && {
          width: '100%',
          padding: `240px ${theme.space.s40}px`,
          backgroundImage: `url(${preFooterBackground})`,
          backgroundPosition: 'right',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundColor: theme.colors.coolGrey[90],
          [theme.breakpoints.bp900.min]: {
            backgroundSize: '100% 100%',
          },
        },
      ]}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container
        css={theme => [
          variant !== 'secondary' && {
            padding: `${theme.space.s64}px ${theme.space.s40}px`,
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: '1fr',
          },
          image?.filename_disk && {
            [theme.breakpoints.bp1024.min]: {
              gridTemplateColumns: '448px 1fr',
            },
          },
        ]}
      >
        <Box
          css={theme => [
            {
              maxWidth: '60em',
              margin: 'auto',
              textAlign: 'center',
            },
            image?.filename_disk && {
              maxWidth: '600px',
              [theme.breakpoints.bp1024.min]: {
                textAlign: 'left',
              },
            },
            variant === 'secondary' && {
              textAlign: 'left',
            },
          ]}
        >
          {heading && (
            <Heading
              as="h2"
              css={theme => [
                hasDarkBackground && { color: theme.colors.white },
                variant === 'secondary' && { maxWidth: '780px', marginBottom: '40px' },
              ]}
              id="preFooterLinkDescription"
              variant={headingVariant || 'headingXL'}
            >
              {heading}
            </Heading>
          )}
          {text && (
            <Text css={theme => hasDarkBackground && { color: theme.colors.white }} variant="paragraphM400">
              {text}
            </Text>
          )}
          {pitch && (
            <Text css={theme => hasDarkBackground && { color: theme.colors.white }} variant="paragraphS400">
              {pitch}
            </Text>
          )}
          {filteredCtas &&
            !hasSSO &&
            filteredCtas.map(({ default_link_text, final_url }, i) => (
              <Link
                key={`pre-footer-ctas_${i}`}
                aria-describedby="preFooterLinkDescription"
                css={theme => ({
                  width: '100%',
                  [theme.breakpoints.bp500.min]: {
                    width: 'auto',
                  },
                })}
                displayAsButton={displayAsButton}
                href={final_url}
                iconAnimation="navigational"
                variant={buttonVariant ?? (hasDarkBackground ? 'secondary' : 'primary')}
              >
                {default_link_text}
              </Link>
            ))}
          {hasSSO && <CtaWithSSO css={{ margin: 'auto' }} hasDarkBackground={hasDarkBackground} />}
        </Box>
        {image?.filename_disk && (
          <Box
            css={theme => ({
              marginTop: theme.space.s24,
              [theme.breakpoints.bp1024.min]: {
                marginTop: 0,
                width: '888px',
                height: '585px',
              },
            })}
          >
            <Image
              css={theme => ({
                width: '100%',
                height: '100%',
                [theme.breakpoints.bp1024.min]: {
                  marginLeft: '140px',
                },
              })}
              imageAlt={image.translations?.[0]?.alt || ''}
              imageSrc={withImage(image)}
              loading="lazy"
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PreFooterCta;

export { PreFooterCta };
